import { AppError } from "../core/errors";
import { idbGet, idbPut, idbDelete } from "./idb";

export interface FolderRef {
  handle: FileSystemDirectoryHandle;
  name: string;
}

const FOLDER_KEY = "save-folder";

export function supportsFileSystemAccess(): boolean {
  return typeof window.showDirectoryPicker === "function";
}

export async function pickSaveFolder(): Promise<FolderRef | null> {
  if (!supportsFileSystemAccess()) {
    throw new AppError("unsupported", "This browser can’t pick a folder; takes go to the app’s own storage.");
  }
  const handle = await window.showDirectoryPicker({
    id: "grabi-takes",
    mode: "readwrite",
    startIn: "videos",
  });
  const ref: FolderRef = { handle, name: handle.name };
  await idbPut("meta", FOLDER_KEY, ref);
  return ref;
}

export async function getSaveFolder(): Promise<FolderRef | null> {
  const stored = await idbGet<FolderRef>("meta", FOLDER_KEY);
  if (!stored?.handle) return null;
  return stored;
}

export async function forgetSaveFolder(): Promise<void> {
  await idbDelete("meta", FOLDER_KEY);
}

/**
 * Permission is re-checked before every recording, never assumed: a stored
 * handle survives a reload, its grant may not.
 */
export async function folderPermission(folder: FolderRef): Promise<PermissionState> {
  const query = folder.handle.queryPermission;
  if (typeof query !== "function") return "granted";
  try {
    return await query.call(folder.handle, { mode: "readwrite" });
  } catch {
    return "prompt";
  }
}

export async function requestFolderPermission(folder: FolderRef): Promise<boolean> {
  const request = folder.handle.requestPermission;
  if (typeof request !== "function") return true;
  try {
    return (await request.call(folder.handle, { mode: "readwrite" })) === "granted";
  } catch {
    return false;
  }
}

export function timestampName(date: Date, extension: string): string {
  const pad = (value: number, width = 2) => String(value).padStart(width, "0");
  const stamp =
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}.${pad(date.getMinutes())}.${pad(date.getSeconds())}`;
  return `Record ${stamp}.${extension}`;
}

/**
 * Streams chunks into a real file, one second at a time. The file is opened
 * before the first frame and closed after the last one, so the recording is
 * on disk the whole time it exists.
 */
export class FileSink {
  private writer: FileSystemWritableFileStream | null = null;
  private handle: FileSystemFileHandle;
  private dir: FileSystemDirectoryHandle;
  bytes = 0;
  readonly fileName: string;

  private constructor(handle: FileSystemFileHandle, fileName: string, dir: FileSystemDirectoryHandle) {
    this.handle = handle;
    this.fileName = fileName;
    this.dir = dir;
  }

  get fileHandle(): FileSystemFileHandle {
    return this.handle;
  }

  static async open(folder: FileSystemDirectoryHandle, fileName: string): Promise<FileSink> {
    const unique = await uniqueName(folder, fileName);
    const handle = await folder.getFileHandle(unique, { create: true });
    const writer = await handle.createWritable({ keepExistingData: false });
    const sink = new FileSink(handle, unique, folder);
    sink.writer = writer;
    return sink;
  }

  async write(chunk: Blob): Promise<void> {
    const writer = this.writer;
    if (!writer) throw new AppError("write", "The file is already closed.");
    await writer.write(chunk);
    this.bytes += chunk.size;
  }

  async close(): Promise<number> {
    const writer = this.writer;
    if (!writer) return this.bytes;
    try {
      await writer.close();
    } finally {
      // Only after the close settles: abort() needs the writer if it failed.
      this.writer = null;
    }
    return this.bytes;
  }

  /** Abandons the file. A partial take still plays; an empty one is removed. */
  async abort(): Promise<void> {
    const writer = this.writer;
    this.writer = null;
    if (writer) {
      try {
        await writer.close();
      } catch {
        // Best effort; the bytes already flushed are kept.
      }
    }
    if (this.bytes === 0) {
      // A failed start leaves a 0-byte file that looks like a recording.
      try {
        await this.dir.removeEntry(this.fileName);
      } catch {
        // Nothing to remove, or the folder is gone.
      }
    }
  }

  async sizeOnDisk(): Promise<number> {
    try {
      const file = await this.handle.getFile();
      return file.size;
    } catch {
      return 0;
    }
  }

  /** Rewrites the file from the durable journal when streaming fell short. */
  async repairFrom(source: Blob): Promise<number> {
    const writer = await this.handle.createWritable({ keepExistingData: false });
    await writer.write(source);
    await writer.close();
    this.bytes = source.size;
    return source.size;
  }
}

async function uniqueName(folder: FileSystemDirectoryHandle, fileName: string): Promise<string> {
  const dot = fileName.lastIndexOf(".");
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  const ext = dot > 0 ? fileName.slice(dot) : "";
  for (let attempt = 1; attempt < 500; attempt++) {
    const candidate = attempt === 1 ? fileName : `${stem} ${attempt}${ext}`;
    try {
      await folder.getFileHandle(candidate, { create: false });
      // Exists: try the next suffix.
    } catch {
      return candidate;
    }
  }
  return `${stem} ${Date.now()}${ext}`;
}
